import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { Document } from "../../common/models/document";
import { OrganizationRole } from "../../common/models/organization-role";
import { User } from "../../common/models/user";
import { AthleteCoach } from "../../common/models/athlete-coach";
import { OrganizationMembership } from "../../common/models/organization-membership";
import { Coach } from "../../common/models/coach";
import { Athlete } from "../../common/models/athlete";
import { AnnualPlanItemRightsList } from "../../annual-plan/providers/annual-plan-rights";

export class DocumentRights {
    public Ready: Promise<boolean> = new Promise((resolve, reject) => resolve(false));
    private _itemRightsList: DocumentItemRightsList | null = null;
    private _screenRightsList: DocumentScreenRightList | null = null;
    private _paramsItem: DocumentItemRightsParams | null = null;
    private _paramsScreen: DocumentScreenRightsParams | null = null;
    private _bestOfItemRight = 10000;
    private _bestOfScreenRight = 10000;
    constructor(private _utils: UtilsProvider) {
        this.setDefault();
    }

    //
    //
    // PUBLIC
    //
    //

    public evaluateItemRight(params: DocumentItemRightsParams): void {

        /**
         * Les paramètres reçus contiennent le document, le user.id
         * et le organizationId. Les droits évalués doivent répondrent
         * à la question : qu'est-ce que je (user.id) peux faire avec
         * ce document (document.id) dans cette organisation (organizationId)
         * avec mon rôle (this._bestOfItemRight).
         */

        this._paramsItem = params;
        this.setDefault();
        this.setBestOfRole();

        const document: Document = this._paramsItem.document;
        const athlete = this._utils.findIn(document.athleteId, this._paramsItem.athletes, 'id');
        const memberships = this._utils.findManyIn([athlete.userId], this._paramsItem.athleteMemberships, 'userId');
        const orgIds = this._utils.getKeyValues(memberships, 'organizationId');


        if(!this._itemRightsList) throw new Error('ItemRightsList is null');
        switch (this._bestOfItemRight) {
        case OrganizationRole.ID_ADMIN:
            // this._paramsItem.user est un admin
            // tous les fichiers
            this._itemRightsList.view = true;
            this._itemRightsList.download = true;
            this._itemRightsList.delete = true;
            break;

        case OrganizationRole.ID_SUPERCOACH:
            // this._paramsItem.user est un supercoach
            // tous les fichiers des athlètes de son organisation
            if (orgIds.indexOf(this._paramsItem.organizationId) > -1) {
                this._itemRightsList.view = true;
                this._itemRightsList.download = true;
                this._itemRightsList.delete = true;
            }
            break;

        case OrganizationRole.ID_COACH:
            // this._paramsItem.user est un coach
            // // tous les documents des athlètes ayant une relation avec lui
            const links = this._utils.findManyIn([athlete.id], this._paramsItem.athleteCoachRelations, 'athleteId');
            const coachIds = this._utils.getKeyValues(links, 'coachId');
            const me = this._utils.findIn(this._paramsItem.user.id, this._paramsItem.coaches, 'userId');

            if (coachIds.indexOf(me.id) > -1) {
                this._itemRightsList.view = true;
                this._itemRightsList.download = true;
                this._itemRightsList.delete = true;
            }
            break;

        case OrganizationRole.ID_ATHLETE:
            // this._paramsItem.user est un athlète
            // uniquement ses documents
            this._itemRightsList.view = (this._paramsItem.user.id === athlete.userId);
            this._itemRightsList.download = (this._paramsItem.user.id === athlete.userId);
            this._itemRightsList.delete = (this._paramsItem.user.id === athlete.userId);
            break;

        case OrganizationRole.ID_GUEST:
            // this._paramsItem.user est un invité
            // aucun
            break;

        default:
            // rien
        }
    }

    public evaluateScreenRights(params: DocumentScreenRightsParams): void {

        /**
         * Détermine les droits que l'utilisateur possède
         * pour les commandes générales des écrans.
         */

        this._paramsScreen = params;
        this.setDefault();
        this.setBestOfRole();

        if(!this._screenRightsList) throw new Error('ScreenRightsList is null');
        switch (this._bestOfScreenRight) {
        case OrganizationRole.ID_ADMIN:
            // this._paramsItem.user est un admin
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            break;

        case OrganizationRole.ID_SUPERCOACH:
            // this._paramsItem.user est un super-coach
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            break;

        case OrganizationRole.ID_COACH:
            // this._paramsItem.user est un coach
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            break;

        case OrganizationRole.ID_ATHLETE:
            // this._paramsItem.user est un athlète
            this._screenRightsList.list = true;
            this._screenRightsList.add = true;
            break;

        case OrganizationRole.ID_GUEST:
            // this._paramsItem.user est un invité
            this._screenRightsList.list = false;
            this._screenRightsList.add = false;
            break;

        default:
            // rien
        }
    }

    public getItemRightsList(): DocumentItemRightsList {
        if(!this._itemRightsList) throw new Error('ItemRightsListghtsList is null');
        return this._itemRightsList;
    }

    public setItemRightsList(doc: Document): void {
        this._itemRightsList = mapDocumentsRightsToItemRights(doc.rights ?? []);
    }

    public getScreenRightsList(): DocumentScreenRightList {
        if(!this._screenRightsList) throw new Error('ScreenRightsList is null');
        return this._screenRightsList;
    }

    public getItemParams(): DocumentItemRightsParams {
        if(!this._paramsItem) throw new Error('ParamsItem is null');
        return this._paramsItem;
    }

    public getScreenParams(): DocumentScreenRightsParams {
        if(!this._paramsScreen) throw new Error('ParamsScreen is null');
        return this._paramsScreen;
    }

    public get LIST() {
        if (this._screenRightsList) {
            return this._screenRightsList.list;
        }
        return false;
    }

    public get ADD() {
        if (this._screenRightsList) {
            return this._screenRightsList.add;
        }
        return false;
    }

    private setDefault(): void {

        const defaultItemRights: DocumentItemRightsList = {
            view: false,
            download: false,
            delete: false,
        };
        this._itemRightsList = defaultItemRights;

        const defaultScreenRights: DocumentScreenRightList = {
            list: false,
            add: false,
        };
        this._screenRightsList = defaultScreenRights;

        this._bestOfItemRight = 10000;
        this._bestOfScreenRight = 10000;
    }

    private setBestOfRole() {

        if (this._paramsItem && this._paramsItem.rolesIds.length > -1) {
            for (let i = 0; i < this._paramsItem.rolesIds.length; i++) {
                if (this._paramsItem.rolesIds[i] < this._bestOfItemRight) {
                    this._bestOfItemRight = this._paramsItem.rolesIds[i];
                }
            }
        }

        if (this._paramsScreen && this._paramsScreen.rolesIds.length > -1) {
            for (let i = 0; i < this._paramsScreen.rolesIds.length; i++) {
                if (this._paramsScreen.rolesIds[i] < this._bestOfScreenRight) {
                    this._bestOfScreenRight = this._paramsScreen.rolesIds[i];
                }
            }
        }
    }
}

export interface DocumentItemRightsList {
    view: boolean;
    download: boolean;
    delete: boolean;
}

const defaultItemRights: DocumentItemRightsList = {
  view: false,
  download: false,
  delete: false,
}

export function mapDocumentsRightsToItemRights(documentsRights: string[]): DocumentItemRightsList {
  const rights = {...defaultItemRights};

  documentsRights.forEach((right: string) => {
    if (right in rights) {
      rights[right as keyof DocumentItemRightsList] = true;
    }
  });

  return rights;
}

export interface DocumentScreenRightList {
    list: boolean;
    add: boolean;
}

export interface DocumentItemRightsParams {
    user: User;
    organizationId: number;
    document: Document;
    rolesIds: number[];
    athleteCoachRelations: AthleteCoach[];
    athleteMemberships: OrganizationMembership[];
    coaches: Coach[];
    athletes: Athlete[];
}

export interface DocumentScreenRightsParams {
    rolesIds: number[];
}
